/**
 * Permission Constants - Phase 11
 * 
 * Single source of truth for frontend RBAC
 */

// Permission Catalog - matches backend
export const PERMISSIONS = {
  // Payments
  'payments:view': { name: 'View Payments', category: 'payments', risk: 'low' },
  'payments:approve': { name: 'Approve Payments', category: 'payments', risk: 'high' },
  'payments:reject': { name: 'Reject Payments', category: 'payments', risk: 'high' },
  
  // Users
  'users:view': { name: 'View Users', category: 'users', risk: 'low' },
  'users:edit': { name: 'Edit Users', category: 'users', risk: 'medium' },
  'users:disable': { name: 'Disable Users', category: 'users', risk: 'high' },
  'users:wallet': { name: 'Adjust Wallets', category: 'users', risk: 'critical' },
  'users:manage': { name: 'Manage Users', category: 'users', risk: 'high' },
  
  // Integrations
  'integrations:view': { name: 'View Integrations', category: 'integrations', risk: 'low' },
  'integrations:configure': { name: 'Configure Integrations', category: 'integrations', risk: 'high' },
  
  // Notifications
  'notifications:broadcast': { name: 'Broadcast Notifications', category: 'notifications', risk: 'medium' },
  
  // Promotions
  'promotions:view': { name: 'View Promotions', category: 'promotions', risk: 'low' },
  'promotions:manage': { name: 'Manage Promotions', category: 'promotions', risk: 'medium' },
  'promotions:notify': { name: 'Send Promotion Notifications', category: 'promotions', risk: 'medium' },
  
  // Security
  'security:view': { name: 'View Security', category: 'security', risk: 'low' },
  'security:manage': { name: 'Manage Security', category: 'security', risk: 'critical' },
  
  // Audit
  'audit:view': { name: 'View Audit Logs', category: 'audit', risk: 'low' },
  
  // Permissions
  'permissions:manage': { name: 'Manage Permissions', category: 'permissions', risk: 'critical' },
  
  // Settings
  'settings:view': { name: 'View Settings', category: 'settings', risk: 'low' },
  'settings:edit': { name: 'Edit Settings', category: 'settings', risk: 'high' },
  
  // Games
  'games:view': { name: 'View Games', category: 'games', risk: 'low' },
  'games:manage': { name: 'Manage Games', category: 'games', risk: 'medium' },
  
  // Analytics
  'analytics:view': { name: 'View Analytics', category: 'analytics', risk: 'low' },
  
  // Providers
  'providers:view': { name: 'View Providers', category: 'providers', risk: 'low' },
  'providers:manage': { name: 'Manage Providers', category: 'providers', risk: 'high' },
  
  // Transactions
  'transactions:view': { name: 'View Transactions', category: 'transactions', risk: 'low' },
  'transactions:export': { name: 'Export Transactions', category: 'transactions', risk: 'medium' },
};

// Role definitions (for matrix display)
export const ROLES = {
  admin: {
    name: 'Administrator',
    description: 'Full system access',
    permissions: Object.keys(PERMISSIONS),
  },
  moderator: {
    name: 'Moderator',
    description: 'User management and payment handling',
    permissions: [
      'users:view', 'users:edit',
      'payments:view', 'payments:approve', 'payments:reject',
      'security:view', 'audit:view', 'analytics:view',
      'transactions:view', 'promotions:view', 'integrations:view',
    ],
  },
  support: {
    name: 'Support',
    description: 'View-only access for customer support',
    permissions: [
      'users:view', 'payments:view', 'audit:view',
      'analytics:view', 'transactions:view',
    ],
  },
};

// Feature gates - which permissions are needed for each feature
export const FEATURE_GATES = {
  // User management actions
  adjustWallet: ['users:wallet'],
  disableUser: ['users:disable'],
  editUser: ['users:edit'],
  viewUsers: ['users:view'],
  
  // Payment actions
  approvePayment: ['payments:approve'],
  rejectPayment: ['payments:reject'],
  viewPayments: ['payments:view'],
  
  // Promotions
  createPromotion: ['promotions:manage'],
  editPromotion: ['promotions:manage'],
  deletePromotion: ['promotions:manage'],
  notifyPromotion: ['promotions:notify'],
  viewPromotions: ['promotions:view'],
  
  // Security
  blockIP: ['security:manage'],
  unblockIP: ['security:manage'],
  viewSecurity: ['security:view'],
  
  // Integrations
  configureIntegrations: ['integrations:configure'],
  testIntegrations: ['integrations:configure'],
  viewIntegrations: ['integrations:view'],
  
  // Notifications
  broadcastNotification: ['notifications:broadcast'],
  
  // Games
  manageGames: ['games:manage'],
  viewGames: ['games:view'],
  
  // Settings
  editSettings: ['settings:edit'],
  viewSettings: ['settings:view'],
  
  // Permissions
  managePermissions: ['permissions:manage'],
  viewPermissionsMatrix: ['audit:view'], // Anyone who can view audit can see matrix
  
  // Analytics
  viewAnalytics: ['analytics:view'],
  
  // Audit
  viewAuditLogs: ['audit:view'],
  
  // Transactions
  viewTransactions: ['transactions:view'],
  exportTransactions: ['transactions:export'],
};

// Route permission requirements
export const ROUTE_PERMISSIONS = {
  '/admin': [], // Dashboard - all staff can see
  '/admin/dashboard': [],
  '/admin/finance': ['analytics:view'],
  '/admin/transactions': ['transactions:view'],
  '/admin/ops-console': ['analytics:view'],
  
  // Phase 11+: Engine-based Operations
  '/admin/orders': [], // All admin can see orders
  '/admin/payments-review': ['payments:view'],
  '/admin/payouts-review': ['payments:view'],
  
  // Payments
  '/admin/payment-methods': ['payments:view'],
  '/admin/deposits': ['payments:view'],
  '/admin/withdrawals': ['payments:view'],
  '/admin/payment-requests': ['payments:view'],
  
  // Games
  '/admin/games': ['games:view'],
  '/admin/game-providers': ['providers:view'],
  
  // Platform
  '/admin/promotions': ['promotions:view'],
  '/admin/achievements': ['games:view'],
  '/admin/referral-tiers': ['analytics:view'],
  '/admin/levels': ['analytics:view'],
  '/admin/referral-analytics': ['analytics:view'],
  '/admin/voided-amounts': ['transactions:view'],
  '/admin/rules': ['settings:view'],
  '/admin/wallet-rules': ['settings:view'],
  '/admin/notifications': ['notifications:broadcast'],
  
  // Integrations
  '/admin/integrations': ['integrations:view'],
  '/admin/integrations-settings': ['integrations:view'],
  '/admin/integrations-setup': ['integrations:configure'],
  '/admin/integration-credentials': ['integrations:configure'],
  '/admin/webhook-events': ['integrations:view'],
  '/admin/webhooks': ['integrations:view'],
  
  // Admin
  '/admin/users': ['users:view'],
  '/admin/security': ['security:view'],
  '/admin/audit-logs': ['audit:view'],
  '/admin/permissions': ['permissions:manage'],
  '/admin/permissions-matrix': ['audit:view'],
  '/admin/staff-management': ['permissions:manage'],
};

// Categories for grouping in matrix view
export const PERMISSION_CATEGORIES = [
  { id: 'payments', name: 'Payments', icon: 'CreditCard' },
  { id: 'users', name: 'Users', icon: 'Users' },
  { id: 'integrations', name: 'Integrations', icon: 'Puzzle' },
  { id: 'notifications', name: 'Notifications', icon: 'Bell' },
  { id: 'promotions', name: 'Promotions', icon: 'Gift' },
  { id: 'security', name: 'Security', icon: 'Shield' },
  { id: 'audit', name: 'Audit', icon: 'FileText' },
  { id: 'permissions', name: 'Permissions', icon: 'Lock' },
  { id: 'settings', name: 'Settings', icon: 'Settings' },
  { id: 'games', name: 'Games', icon: 'Gamepad2' },
  { id: 'analytics', name: 'Analytics', icon: 'BarChart3' },
  { id: 'providers', name: 'Providers', icon: 'Puzzle' },
  { id: 'transactions', name: 'Transactions', icon: 'FileText' },
];

export default PERMISSIONS;
